'use client';

import { Icon } from '@/components/ui';
import { useAriaLabels } from '@/hooks/useAriaLabels';
import { useHeroSliderPromotionTracking } from '@/hooks/useHeroSliderPromotionTracking';
import { useImageLoader } from '@/hooks/useImageLoader';
import clsx from 'clsx';
import Image from 'next/image';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useSlideNavigation } from '../HeroSlider.hooks';
import styles from '../HeroSlider.module.scss';
import {
  getHeroThumbnailCssVars,
  HERO_BACKGROUND_SIZES,
  HERO_THUMBNAIL_DIMENSIONS,
} from '../HeroSlider.constants';
import type { BaseHeroSliderProps } from '../HeroSlider.types';
import { getSlideMedia } from '../HeroSlider.utils';
import {
  animationReducer,
  EXPAND_CLEANUP_DELAY,
  EXPAND_SWITCH_DELAY,
  INITIAL_ANIMATION_STATE,
  isAnimating,
  isExpandPhase,
  isShrinkPhase,
  SHRINK_CLEANUP_DELAY,
  SHRINK_COLLAPSE_DELAY,
  SHRINK_REVEAL_DELAY,
} from './DynamicHeroSlider.animation';
import { SlideContent } from './SlideContent';
import { ThumbnailStrip } from './ThumbnailStrip';

export const DynamicHeroSlider = ({
  slides,
  autoplay = false,
  interval = 5000,
  fixToTop = false,
  preload,
  cmsIndex,
}: BaseHeroSliderProps) => {
  const aria = useAriaLabels();
  const testIdPrefix = cmsIndex !== undefined ? `heroslider-${cmsIndex}` : undefined;
  const testId = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);
  const { focalPointLoader } = useImageLoader();

  const {
    activeIndex,
    hasMultipleSlides,
    setIsPaused,
    isPaused,
    goToSlide,
    goToPrev: _goToPrev,
    goToNext: _goToNext,
  } = useSlideNavigation(slides.length, false, interval);

  const setContainerRef = useHeroSliderPromotionTracking({ slides, activeIndex });

  const heroRef = useRef<HTMLElement | null>(null);
  const setHeroRef = useCallback(
    (node: HTMLElement | null) => {
      heroRef.current = node;
      setContainerRef(node);
    },
    [setContainerRef]
  );
  const stripRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<NodeJS.Timeout[]>([]);
  const [animation, dispatch] = useReducer(animationReducer, INITIAL_ANIMATION_STATE);

  const expanding = isExpandPhase(animation.phase);
  const shrinking = isShrinkPhase(animation.phase);
  const busy = isAnimating(animation.phase);
  const pendingIndexRef = useRef(activeIndex);
  const prevActiveIndexRef = useRef(activeIndex);
  if (activeIndex !== prevActiveIndexRef.current) {
    pendingIndexRef.current = activeIndex;
    prevActiveIndexRef.current = activeIndex;
  }

  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
    };
  }, []);

  const handleSlideSelect = useCallback(
    (index: number, thumbnailRect?: DOMRect) => {
      if (thumbnailRect && heroRef.current) {
        const heroRect = heroRef.current.getBoundingClientRect();
        const slide = slides[index];
        const { imageUrl, objectPosition } = getSlideMedia(slide);

        if (imageUrl) {
          timersRef.current.forEach(clearTimeout);
          timersRef.current = [];

          dispatch({
            type: 'EXPAND_START',
            thumb: {
              slideIndex: index,
              imageUrl,
              objectPosition,
              top: thumbnailRect.top - heroRect.top,
              left: thumbnailRect.left - heroRect.left,
              width: thumbnailRect.width,
              height: thumbnailRect.height,
            },
          });

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              dispatch({ type: 'EXPAND_GROW' });
            });
          });

          const slideTimer = setTimeout(() => {
            goToSlide(index);
            dispatch({ type: 'EXPAND_SWITCH' });
          }, EXPAND_SWITCH_DELAY);

          const cleanupTimer = setTimeout(() => {
            dispatch({ type: 'EXPAND_CLEANUP' });
          }, EXPAND_CLEANUP_DELAY);

          timersRef.current.push(slideTimer, cleanupTimer);
          return;
        }
      }

      goToSlide(index);
    },
    [goToSlide, slides]
  );

  const handlePrevSlide = useCallback(() => {
    if (!heroRef.current || !stripRef.current) return;

    const currentIndex = pendingIndexRef.current;
    const prevIndex = (currentIndex - 1 + slides.length) % slides.length;
    pendingIndexRef.current = prevIndex;

    const visibleSlide = slides[activeIndex];
    const { imageUrl, objectPosition } = getSlideMedia(visibleSlide);

    if (!imageUrl) {
      goToSlide(prevIndex);
      return;
    }

    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    const heroRect = heroRef.current.getBoundingClientRect();
    const stripRect = stripRef.current.getBoundingClientRect();
    const firstThumb = stripRef.current.querySelector<HTMLButtonElement>('[role="tab"]');

    const oldActiveIndex = activeIndex;

    dispatch({
      type: 'SHRINK_START',
      thumb: {
        slideIndex: activeIndex,
        imageUrl,
        objectPosition,
        top: stripRect.top - heroRect.top,
        left: stripRect.left - heroRect.left,
        width: firstThumb?.offsetWidth || HERO_THUMBNAIL_DIMENSIONS.mobile.width,
        height: firstThumb?.offsetHeight || HERO_THUMBNAIL_DIMENSIONS.mobile.height,
      },
      fadingOutIndex: activeIndex,
    });

    const collapseTimer = setTimeout(() => {
      goToSlide(prevIndex);
      dispatch({ type: 'SHRINK_COLLAPSE', enteringIndex: oldActiveIndex });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dispatch({ type: 'SHRINK_GROW' });
        });
      });
    }, SHRINK_COLLAPSE_DELAY);

    const revealTimer = setTimeout(() => {
      dispatch({ type: 'SHRINK_REVEAL' });
    }, SHRINK_REVEAL_DELAY);

    const cleanupTimer = setTimeout(() => {
      dispatch({ type: 'SHRINK_CLEANUP' });
    }, SHRINK_CLEANUP_DELAY);

    timersRef.current.push(collapseTimer, revealTimer, cleanupTimer);
  }, [activeIndex, slides, goToSlide]);

  const handleArrow = useCallback(
    (direction: 'prev' | 'next') => {
      if (direction === 'prev') {
        handlePrevSlide();
        return;
      }

      const targetIndex = (pendingIndexRef.current + 1) % slides.length;
      pendingIndexRef.current = targetIndex;
      const button = stripRef.current?.querySelector<HTMLButtonElement>(
        `[data-slide-index="${targetIndex}"]`
      );
      if (button) button.click();
    },
    [slides.length, handlePrevSlide]
  );

  // Autoplay: trigger the same thumbnail expansion animation as clicking next
  useEffect(() => {
    if (!autoplay || !hasMultipleSlides || isPaused || busy) {
      return;
    }

    const autoplayTimer = setTimeout(() => {
      const targetIndex = (activeIndex + 1) % slides.length;
      const button = stripRef.current?.querySelector<HTMLButtonElement>(
        `[data-slide-index="${targetIndex}"]`
      );
      if (button) button.click();
    }, interval);

    return () => clearTimeout(autoplayTimer);
  }, [autoplay, hasMultipleSlides, isPaused, busy, activeIndex, slides.length, interval]);

  if (slides.length === 0) {
    return null;
  }

  return (
    <section
      ref={setHeroRef}
      className={clsx(
        styles.dynamicHero,
        fixToTop && styles.fixedTop,
        expanding && styles.isExpanding,
        animation.contentRaised && styles.contentRaised,
        shrinking && styles.isShrinking
      )}
      style={getHeroThumbnailCssVars()}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      aria-label={aria.action('featuredArticles')}
      data-testid={testId('section')}
    >
      <div className={styles.dynamicSlides}>
        {slides.map((slide, index) => {
          const desktopUrl = slide.media?.desktop?.url;
          const mobileUrl = slide.media?.mobile?.url;
          const desktopFocal = slide.media?.desktop?.focalPoint ?? null;
          const mobileFocal = slide.media?.mobile?.focalPoint ?? null;
          const desktopWidth = slide.media?.desktop?.width ?? undefined;
          const desktopHeight = slide.media?.desktop?.height ?? undefined;
          const mobileWidth = slide.media?.mobile?.width ?? undefined;
          const mobileHeight = slide.media?.mobile?.height ?? undefined;
          const desktopPosition = desktopFocal ? `${desktopFocal.x}% ${desktopFocal.y}%` : 'center';
          const mobilePosition = mobileFocal ? `${mobileFocal.x}% ${mobileFocal.y}%` : 'center';
          const alt = slide.heading || `Slide ${index + 1}`;
          const isPriority = preload && index === 0;
          const hasSeparateMobile = Boolean(mobileUrl && mobileUrl !== desktopUrl);

          return (
            <div
              key={slide.sys?.id || index}
              className={clsx(styles.dynamicSlide, index === activeIndex && styles.active)}
              aria-hidden={index !== activeIndex}
            >
              <div className={styles.dynamicBackground}>
                {hasSeparateMobile && mobileUrl && (
                  <Image
                    src={mobileUrl}
                    alt={alt}
                    fill
                    className={clsx(styles.dynamicBackgroundImage, styles.mobileOnly)}
                    sizes={HERO_BACKGROUND_SIZES.mobileOnly}
                    priority={isPriority}
                    loader={focalPointLoader(
                      mobileFocal,
                      mobileWidth && mobileHeight ? { x: mobileWidth, y: mobileHeight } : undefined
                    )}
                    style={{ objectPosition: mobilePosition }}
                  />
                )}
                {desktopUrl && (
                  <Image
                    src={desktopUrl}
                    alt={alt}
                    fill
                    className={clsx(
                      styles.dynamicBackgroundImage,
                      hasSeparateMobile && styles.desktopOnly
                    )}
                    sizes={
                      hasSeparateMobile
                        ? HERO_BACKGROUND_SIZES.desktopOnly
                        : HERO_BACKGROUND_SIZES.responsive
                    }
                    priority={isPriority}
                    loader={focalPointLoader(
                      desktopFocal,
                      desktopWidth && desktopHeight
                        ? { x: desktopWidth, y: desktopHeight }
                        : undefined
                    )}
                    style={{ objectPosition: desktopPosition }}
                  />
                )}
              </div>
            </div>
          );
        })}

        {animation.expandingThumb && (
          <div
            key={`expand-${animation.animationId}`}
            className={clsx(
              styles.expandingOverlay,
              animation.isExpanded && styles.expandingOverlayExpanded
            )}
            style={
              {
                '--thumb-top': `${animation.expandingThumb.top}px`,
                '--thumb-left': `${animation.expandingThumb.left}px`,
                '--thumb-width': `${animation.expandingThumb.width}px`,
                '--thumb-height': `${animation.expandingThumb.height}px`,
              } as React.CSSProperties
            }
            aria-hidden="true"
          >
            <Image
              src={animation.expandingThumb.imageUrl}
              alt=""
              fill
              className={styles.dynamicBackgroundImage}
              sizes="100vw"
              style={{ objectPosition: animation.expandingThumb.objectPosition }}
            />
          </div>
        )}

        {animation.shrinkingThumb && (
          <div
            key={`shrink-${animation.animationId}`}
            className={clsx(
              styles.shrinkingOverlay,
              animation.isShrunk && styles.shrinkingOverlayShrunk
            )}
            style={
              {
                '--target-top': `${animation.shrinkingThumb.top}px`,
                '--target-left': `${animation.shrinkingThumb.left}px`,
                '--target-width': `${animation.shrinkingThumb.width}px`,
                '--target-height': `${animation.shrinkingThumb.height}px`,
              } as React.CSSProperties
            }
            aria-hidden="true"
          >
            <Image
              src={animation.shrinkingThumb.imageUrl}
              alt=""
              fill
              className={styles.dynamicBackgroundImage}
              sizes="100vw"
              style={{ objectPosition: animation.shrinkingThumb.objectPosition }}
            />
          </div>
        )}

        <div
          className={clsx(
            styles.dynamicOverlay,
            animation.hideGradient && styles.dynamicOverlayHidden
          )}
          aria-hidden="true"
        />

        {slides.map((slide, index) => (
          <SlideContent
            key={slide.sys?.id || `content-${index}`}
            slide={slide}
            slideIndex={index}
            isActive={index === activeIndex}
            testId={testId}
          />
        ))}
      </div>

      {hasMultipleSlides && (
        <div className={styles.thumbnailSection}>
          <ThumbnailStrip
            slides={slides}
            activeIndex={activeIndex}
            onSelect={handleSlideSelect}
            testId={testId}
            stripRef={stripRef}
            enteringIndex={animation.enteringIndex}
            enteringVisible={animation.enteringVisible}
            fadingOutIndex={animation.fadingOutIndex}
          />
          <div className={styles.dynamicNavControls} data-testid={testId('nav-controls')}>
            <div className={styles.dynamicProgressBar} aria-hidden="true">
              <div
                className={styles.dynamicProgressFill}
                style={{
                  width: `${100 / slides.length}%`,
                  left: `${(activeIndex / slides.length) * 100}%`,
                }}
              />
            </div>
            <div className={styles.dynamicArrows}>
              <button
                type="button"
                className={styles.dynamicArrowButton}
                onClick={() => handleArrow('prev')}
                aria-label={aria.action('previousSlide')}
                data-testid={testId('prev')}
              >
                <Icon name="chevron-left" size={20} />
              </button>
              <button
                type="button"
                className={styles.dynamicArrowButton}
                onClick={() => handleArrow('next')}
                aria-label={aria.action('nextSlide')}
                data-testid={testId('next')}
              >
                <Icon name="chevron-right" size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

DynamicHeroSlider.displayName = 'DynamicHeroSlider';
